/**
 * Tracker — body-weight and BMI progress charts with the weight-logging entry
 * point (mobile drawer/trackProgress parity).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useProgressChart, {
  type UseProgressChartResult,
} from '@wlb/shared/hooks/useProgressChart';
import { ToastService } from '@wlb/shared/platform/notifier';
import { calculateBmi, getBmiRange, type ChartPeriod } from '@wlb/shared/utils/progress';
import {
  normalizeWeightUnit,
  type WeightUnitCode,
} from '@wlb/shared/utils/weightUtils';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { Tabs } from '../components/ui/Tabs';
import { ChartIcon, WeightScaleIcon } from '../components/ui/icons';
import { MorePage } from '../features/more/MorePage';
import { MoreLoading } from '../features/more/MoreStatus';
import { ProgressChart } from '../features/more/ProgressChart';
import { UpdateWeightModal } from '../features/more/UpdateWeightModal';
import '../features/more/more.css';

const PERIODS: readonly ChartPeriod[] = ['daily', 'weekly', 'monthly'];

/** Each BMI band owns a colour token so the bar and the line stay in sync. */
const BMI_COLORS = {
  underweight: 'var(--color-info)',
  normal: 'var(--color-success)',
  overweight: 'var(--color-warning)',
  obese: 'var(--color-error)',
} as const;

interface TrackerCardProps {
  title: string;
  subtitle: string;
  badge?: string;
  chart: UseProgressChartResult;
  color: string;
  chartLabel: string;
  /** Namespaces the period tab ids so both cards can render tabs. */
  idPrefix: string;
  icon: ReactNode;
  children?: ReactNode;
}

function TrackerCard({
  title,
  subtitle,
  badge,
  chart,
  color,
  chartLabel,
  idPrefix,
  icon,
  children,
}: TrackerCardProps) {
  const { t } = useTranslation();

  const periodItems = useMemo(
    () => PERIODS.map(key => ({ key, label: t(`tracker.tabs.${key}`) })),
    [t],
  );

  const renderChart = () => {
    if (chart.loading && chart.values.length === 0) return <MoreLoading variant="card" />;
    if (chart.error) {
      return (
        <ListStatus
          variant="error"
          message={chart.error || t('tracker.chart.error')}
          onAction={chart.refresh}
          busy={chart.loading}
        />
      );
    }
    if (chart.values.length === 0) {
      return <p className="more-status">{t('tracker.chart.empty')}</p>;
    }
    return (
      <ProgressChart
        values={chart.values}
        labels={chart.labels}
        color={color}
        ariaLabel={chartLabel}
      />
    );
  };

  return (
    <section className="more-card">
      <header className="tracker-card__header">
        <span className="awards-level__icon" aria-hidden>{icon}</span>
        <div className="tracker-card__heading">
          <h2 className="tracker-card__title">{title}</h2>
          <p className="tracker-card__subtitle">{subtitle}</p>
        </div>
        {badge && <span className="tracker-card__badge">{badge}</span>}
      </header>

      {children}
      {renderChart()}

      <Tabs
        items={periodItems}
        activeKey={chart.period}
        onChange={chart.setPeriod}
        label={t('tracker.accessibility.periodTabs')}
        idPrefix={idPrefix}
      />
    </section>
  );
}

export function Tracker() {
  const { t } = useTranslation();
  const { profile, updateMeasurement } = useProfile();
  const weightChart = useProgressChart('weight');
  const bmiChart = useProgressChart('bmi');
  const [weightModalOpen, setWeightModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const unit = normalizeWeightUnit(profile?.unit);
  const currentWeight = profile?.currentWeight ?? null;
  const bmi = calculateBmi(currentWeight, profile?.heightInches, unit);
  const bmiRange = getBmiRange(bmi);
  const bmiColor = BMI_COLORS[bmiRange.key];

  const handleSaveWeight = async (weight: number, nextUnit: WeightUnitCode) => {
    setSaving(true);
    try {
      await updateMeasurement(weight, nextUnit);
      setWeightModalOpen(false);
      weightChart.refresh();
      bmiChart.refresh();
    } catch {
      ToastService.show({ type: 'error', message: t('tracker.bottomSheet.errorToast') });
    } finally {
      setSaving(false);
    }
  };

  const rangeLabel = t(`tracker.bmi.ranges.${bmiRange.key}`);

  return (
    <MorePage title={t('tracker.title')}>
      <section className="tracker-banner">
        <div className="tracker-banner__copy">
          <p className="tracker-banner__title">{t('tracker.updateWeightCard.title')}</p>
          <p className="tracker-banner__subtitle">{t('tracker.updateWeightCard.subtitle')}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setWeightModalOpen(true)}>
          {t('tracker.updateWeightCard.button')}
        </Button>
      </section>

      <TrackerCard
        title={t('tracker.bodyWeight.title')}
        subtitle={t('tracker.bodyWeight.subtitle')}
        badge={
          currentWeight
            ? `${currentWeight} ${t(unit === 'kg' ? 'tracker.bodyWeight.unitKg' : 'tracker.bodyWeight.unitLbs')}`
            : undefined
        }
        chart={weightChart}
        color="var(--color-primary)"
        chartLabel={t('tracker.accessibility.weightChart')}
        idPrefix="tracker-weight-period"
        icon={<WeightScaleIcon size={20} />}
      >
        {!currentWeight && <p className="tracker-card__subtitle">{t('tracker.noWeight')}</p>}
      </TrackerCard>

      <TrackerCard
        title={t('tracker.bmi.title')}
        subtitle={t('tracker.bmi.subtitle')}
        badge={bmi != null ? `${bmi} ${t('tracker.bmi.badge')}` : undefined}
        chart={bmiChart}
        color={bmiColor}
        chartLabel={t('tracker.accessibility.bmiChart')}
        idPrefix="tracker-bmi-period"
        icon={<ChartIcon size={20} />}
      >
        <div className="tracker-scale">
          <div className="tracker-scale__labels" aria-hidden>
            <span>{t('tracker.bmi.ranges.underweight')}</span>
            <span>{t('tracker.bmi.ranges.normal')}</span>
            <span>{t('tracker.bmi.ranges.overweight')}</span>
            <span>{t('tracker.bmi.ranges.obese')}</span>
          </div>
          <div
            className="tracker-scale__bar"
            role="img"
            aria-label={`${t('tracker.accessibility.bmiScale')}: ${rangeLabel}`}
          >
            {(Object.keys(BMI_COLORS) as Array<keyof typeof BMI_COLORS>).map(key => (
              <span
                key={key}
                className="tracker-scale__segment"
                style={{ background: BMI_COLORS[key] }}
              />
            ))}
            {bmi != null && (
              <span
                className="tracker-scale__marker"
                style={{ left: `${bmiRange.percent}%`, background: bmiColor }}
              />
            )}
          </div>
          {bmi != null && (
            <p className="tracker-scale__status">
              <span className="tracker-scale__status-value" style={{ color: bmiColor }}>
                {t('tracker.bmi.ranges.current', { range: rangeLabel })}
              </span>
            </p>
          )}
        </div>
      </TrackerCard>

      {weightModalOpen && (
        <UpdateWeightModal
          initialWeight={currentWeight}
          initialUnit={unit}
          saving={saving}
          onClose={() => setWeightModalOpen(false)}
          onSave={(weight, nextUnit) => { handleSaveWeight(weight, nextUnit).catch(() => undefined); }}
        />
      )}
    </MorePage>
  );
}
